import { useEffect, useRef, useState } from 'react';
import { captureAnalytics } from '../../../actions/preload';
import {
  addCallFollowUpEntry,
  updateCallFollowUp,
} from './callFollowUpApi';
import {
  blankEntryForm,
  changedFollowUpFields,
  formFromFollowup,
  normalizeId,
  safeFollowUpErrorMessage,
} from './callFollowUpFormUtils';
import runGuardedRequest from './guardedRequest';

const useCallFollowUpEditor = ({
  followup,
  onFollowUpChanged = () => {},
  analyticsSurface = 'call_followup_editor',
}) => {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(formFromFollowup(followup));
  const [entryForm, setEntryForm] = useState(blankEntryForm);
  const followupRef = useRef(followup);
  const writeRequestIdRef = useRef(0);

  followupRef.current = followup;

  const resetDraftState = (nextFollowup = followupRef.current) => {
    setSaving(false);
    setSaveError(null);
    setEditMode(false);
    setEditForm(formFromFollowup(nextFollowup));
    setEntryForm(blankEntryForm);
  };

  useEffect(() => {
    writeRequestIdRef.current += 1;
    resetDraftState(followup);

    return () => {
      writeRequestIdRef.current += 1;
    };
  }, [followup?.id]);

  const beginWriteRequest = () => {
    const requestId = writeRequestIdRef.current + 1;
    writeRequestIdRef.current = requestId;

    return {
      requestId,
      followUpId: followupRef.current?.id,
    };
  };

  const writeRequestStillCurrent = (requestContext) => (
    requestContext.followUpId
    && requestContext.requestId === writeRequestIdRef.current
    && normalizeId(followupRef.current?.id) === normalizeId(requestContext.followUpId)
  );

  const applySavedFollowUp = (nextFollowup) => {
    const mergedFollowup = {
      ...followupRef.current,
      ...nextFollowup,
      completed: nextFollowup.completed !== undefined ? nextFollowup.completed : !!nextFollowup.completed_at,
      database_call_log: nextFollowup.database_call_log || followupRef.current?.database_call_log,
    };
    followupRef.current = mergedFollowup;
    onFollowUpChanged(mergedFollowup);
    return mergedFollowup;
  };

  const cancelEdit = () => {
    setEditForm(formFromFollowup(followupRef.current));
    setEditMode(false);
  };

  const trackAssigneeChange = (changes) => {
    if (!Object.prototype.hasOwnProperty.call(changes, 'assignee_id')) return;

    captureAnalytics(
      changes.assignee_id ? 'call_followup_assign_assignee' : 'call_followup_clear_assignee',
      { surface: analyticsSurface },
    );
  };

  const persistDetailForm = async (nextEditForm, closeEditMode = true) => {
    setSaveError(null);
    const currentFollowup = followupRef.current;
    const changes = changedFollowUpFields(nextEditForm, currentFollowup);
    if (Object.keys(changes).length === 0) {
      if (closeEditMode) setEditMode(false);
      return undefined;
    }

    const requestContext = beginWriteRequest();
    return runGuardedRequest({
      isCurrent: () => writeRequestStillCurrent(requestContext),
      onStart: () => setSaving(true),
      request: () => updateCallFollowUp(currentFollowup.id, changes),
      onSuccess: (updated) => {
        const mergedFollowup = applySavedFollowUp(updated);
        trackAssigneeChange(changes);
        setEditForm(formFromFollowup(mergedFollowup));
        if (closeEditMode) setEditMode(false);
      },
      onError: (updateError) => setSaveError(safeFollowUpErrorMessage(updateError)),
      onFinally: () => setSaving(false),
    });
  };

  const updateDetails = async () => persistDetailForm(editForm, true);

  const addEntry = async () => {
    setSaveError(null);
    if (!entryForm.statusId) {
      setSaveError('Status is required before adding an entry.');
      return false;
    }

    const currentFollowup = followupRef.current;
    const requestContext = beginWriteRequest();
    let saved = false;
    await runGuardedRequest({
      isCurrent: () => writeRequestStillCurrent(requestContext),
      onStart: () => setSaving(true),
      request: () => addCallFollowUpEntry(currentFollowup.id, {
        call_followup_status_id: entryForm.statusId,
        note: entryForm.note,
      }),
      onSuccess: (updated) => {
        applySavedFollowUp(updated);
        setEntryForm((form) => ({ ...form, note: '' }));
        saved = true;
      },
      onError: (addError) => setSaveError(safeFollowUpErrorMessage(addError, 'Unable to add follow-up entry. Please try again.')),
      onFinally: () => setSaving(false),
    });
    return saved;
  };

  return {
    saving,
    saveError,
    editMode,
    setEditMode,
    editForm,
    setEditForm,
    entryForm,
    setEntryForm,
    cancelEdit,
    updateDetails,
    addEntry,
  };
};

export default useCallFollowUpEditor;
